// Currency formatting utilities — isomorphic (browser + Node), no i18n
// singleton: the viewer's locale/language is always an explicit argument.
import { localeForLanguage } from "./locale.js";
// ECB-supported currencies (base: EUR)
export const SUPPORTED_CURRENCIES = [
    'EUR', 'USD', 'GBP', 'JPY', 'CHF', 'AUD', 'CAD', 'SEK', 'NOK', 'DKK',
    'PLN', 'CZK', 'HUF', 'BGN', 'RON', 'ISK', 'TRY', 'BRL', 'CNY', 'HKD',
    'IDR', 'ILS', 'INR', 'KRW', 'MXN', 'MYR', 'NZD', 'PHP', 'SGD', 'THB', 'ZAR'
];
/**
 * The curated list an organization may pick as its BASE currency: the majors
 * plus every European currency the ECB feed carries. This is a UX choice for
 * the org-settings picker only — it is NOT the ledger allowlist. Accounts,
 * imports and documents may use any of `SUPPORTED_CURRENCIES`; an org already
 * on a code outside this list keeps it.
 */
export const ORG_BASE_CURRENCIES = [
    // Majors
    'EUR', 'USD', 'GBP', 'CHF', 'CAD', 'AUD',
    // Europe outside the euro area (ECB reference currencies)
    'DKK', 'NOK', 'SEK', 'ISK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN',
];
/** Shape of a well-formed ISO 4217 code after trim + uppercase. */
const ISO_4217_CODE = /^[A-Z]{3}$/;
/**
 * Trim + uppercase a currency code, returning '' when it's blank, whitespace,
 * or nullish. Building block for the public resolvers below.
 */
function cleanCurrencyCode(currency) {
    return currency?.trim().toUpperCase() ?? '';
}
/**
 * Normalize a single currency code for `Intl.NumberFormat`: trims + uppercases,
 * falling back to 'EUR' when blank. Imported bank-feed rows (notably CSV
 * imports) can land an empty-string `currency`, and `Intl.NumberFormat` throws
 * `RangeError: Invalid currency code` on `''` — normalizing here keeps every
 * formatter that funnels through this module crash-safe.
 */
export function normalizeCurrencyCode(currency) {
    return cleanCurrencyCode(currency) || 'EUR';
}
/**
 * Pick the first WELL-FORMED currency code from a priority list, trimmed +
 * uppercased. A candidate is skipped when it is blank / whitespace / nullish
 * OR when it is not three ASCII letters (`'€'`, `'US'`, `'usdx'`) — those are
 * display-fallback chains (explicit → account → org), and a malformed code
 * from a scanned document or a CSV row must degrade to the next source, not
 * crash `Intl.NumberFormat`. Well-formed here means the shape only: `'XYZ'`
 * passes and Intl formats it as `XYZ 1.00`. Falls back to 'EUR' when every
 * candidate is skipped. Shared by `CurrencyDisplay` and
 * `resolveImportCurrency` so every surface resolves currency the same way.
 */
export function resolveCurrency(candidates) {
    for (const candidate of candidates) {
        const cleaned = cleanCurrencyCode(candidate);
        if (ISO_4217_CODE.test(cleaned))
            return cleaned;
    }
    return 'EUR';
}
/**
 * Resolve the effective display currency for a bank-feed import row: the row's
 * own currency, else its bank account's, else the organization's, else 'EUR'.
 * Blank values (CSV imports land `''`) are skipped. Used by the Split and
 * Create-Expense dialogs and the clearing list cells so a single row shows one
 * consistent currency everywhere.
 */
export function resolveImportCurrency(item, orgCurrency) {
    return resolveCurrency([item?.currency, item?.bank_account?.currency, orgCurrency]);
}
/**
 * Resolve the ledger currency of a chart-of-accounts row: the account's own
 * currency when set, else the organization base currency, else 'EUR'. This is
 * the currency an entry posted to that account is denominated in — `currency`
 * is NULL on most accounts (expense categories, AR/AP, tax), which means "org
 * base", not "any". Only FX-clearing-style pass-through accounts are genuinely
 * currency-agnostic, and those never go through this resolver in a gate.
 */
export function resolveAccountCurrency(account, orgCurrency) {
    return resolveCurrency([account?.currency, orgCurrency]);
}
/**
 * True when a bank-import row's resolved currency equals the currency of the
 * bank account it lands in. Blank import currencies (CSV imports land `''`)
 * resolve to the account currency, so they always match. A genuine
 * cross-currency row — e.g. a USD wire booked into a EUR account — returns
 * false.
 *
 * This is only HALF of the posting gate: it validates the bank leg. The other
 * leg (the offset/expense account) must ALSO be denominated in the same
 * currency — cross-currency journal entries are only allowed through the FX
 * clearing account (see docs/MULTI_CURRENCY.md). Callers that post entries
 * must additionally compare against resolveAccountCurrency(offsetAccount).
 */
export function importMatchesAccountCurrency(item, orgCurrency) {
    return (resolveImportCurrency(item, orgCurrency) ===
        resolveCurrency([item?.bank_account?.currency, orgCurrency]));
}
/**
 * Format an amount using the specified currency
 * @param amount - The numeric amount to format
 * @param currency - ISO 4217 currency code (e.g., 'EUR', 'USD', 'GBP')
 * @param locale - Locale for formatting (see `localeForLanguage`)
 * @returns Formatted currency string
 */
export function formatCurrency(amount, currency, locale) {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: normalizeCurrencyCode(currency),
    }).format(amount);
}
/**
 * Get the currency symbol for a given currency code
 * @param currency - ISO 4217 currency code
 * @param locale - Optional locale for symbol formatting
 * @returns Currency symbol (e.g., '€', '$', '£')
 */
export function getCurrencySymbol(currency = 'EUR', locale = 'en-US') {
    const code = normalizeCurrencyCode(currency);
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
    }).formatToParts(0).find(part => part.type === 'currency')?.value || code;
}
/**
 * Localized display name for a currency code ("Euro", "Canadese dollar"),
 * from `Intl.DisplayNames` — the single source for currency names, so no
 * name table lives in the app or the database. Falls back to the code when
 * the runtime has no name for it (`'XYZ'`) or rejects it (`'ZZ'`).
 * @param currency - ISO 4217 currency code
 * @param locale - Locale for the name (see `localeForLanguage`)
 */
export function getCurrencyName(currency = 'EUR', locale = 'en-US') {
    const code = normalizeCurrencyCode(currency);
    try {
        return new Intl.DisplayNames([locale], { type: 'currency' }).of(code) || code;
    }
    catch {
        return code;
    }
}
/**
 * Format an amount with organization's default currency using proper locale formatting
 * @param amount - The numeric amount to format
 * @param organization - Organization object with currency field
 * @param language - Viewer/document language driving the number convention
 * @returns Formatted currency string with locale-appropriate number formatting
 */
export function formatOrganizationCurrency(amount, organization, language) {
    const currency = organization?.currency || 'EUR';
    return formatCurrency(amount, currency, localeForLanguage(language));
}
/**
 * Format an amount respecting account-level currency override
 * Account currency takes precedence over organization currency
 * @param amount - The numeric amount to format
 * @param account - Account object with optional currency field
 * @param organization - Organization object with currency field (fallback)
 * @param language - Viewer/document language driving the number convention
 * @returns Formatted currency string with locale-appropriate number formatting
 */
export function formatAccountCurrency(amount, account, organization, language) {
    // Account currency takes precedence if set
    const currency = account?.currency || organization?.currency || 'EUR';
    return formatCurrency(amount, currency, localeForLanguage(language));
}
/**
 * Find the entry on the debit or credit side of a ledger transaction.
 * Used to identify the "AR side" (debit / asset) vs "AP side" (credit /
 * liability) of an invoice transaction, or the source (credit) vs
 * destination (debit) of a transfer.
 *
 * @param entries Ledger transaction entries
 * @param side 'debit' picks the first entry with debit_amount > 0; 'credit' picks first with credit_amount > 0
 * @param predicate Optional extra filter (e.g. require non-null currency, or account-name match)
 */
export function findEntryBySide(entries, side, predicate) {
    if (!entries)
        return undefined;
    return entries.find((e) => {
        const sideMatch = side === 'debit' ? e.debit_amount > 0 : e.credit_amount > 0;
        if (!sideMatch)
            return false;
        return predicate ? predicate(e) : true;
    });
}
/**
 * Get the currency of the account on the chosen side of a ledger transaction.
 * Returns null if no matching entry is found or the account has no currency set.
 *
 * @param entries Ledger transaction entries
 * @param side Which side to read ('debit' for AR/asset, 'credit' for AP/liability)
 */
export function getEntryAccountCurrency(entries, side) {
    return findEntryBySide(entries, side)?.chart_of_accounts?.currency ?? null;
}
/**
 * The journal's currency from its embedded legs — the TypeScript twin of the
 * `currency` aggregate on `ledger_transaction_index` (migration
 * 20260917154036), for rows that never went through the view. Keep the two
 * in step; both are proven against the same shapes (the view's manual test
 * and this module's unit tests):
 *   - a normal journal resolves every leg to ONE currency (an account with
 *     none is in the org currency; the balance trigger refuses a mix) → that
 *     code, or null when every leg is org-currency;
 *   - a currency transfer spans two through fx_clearing, and `total_amount`
 *     is the SOURCE-side figure → the credited non-fx_clearing leg's
 *     currency. Ties (only a legacy pre-gate mixed journal can have several
 *     such legs) break on the largest credit, as the SQL does.
 * Entries whose account embed lacks `currency` read as org-currency, so an
 * older select degrades to the org fallback rather than a wrong code.
 */
export function journalCurrencyFromEntries(entries) {
    if (!entries || entries.length === 0)
        return null;
    const accountOf = (e) => e.account ?? e.chart_of_accounts ?? null;
    const effective = new Set(entries.map((e) => accountOf(e)?.currency ?? ''));
    if (effective.size <= 1) {
        return entries.map((e) => accountOf(e)?.currency).find(Boolean) ?? null;
    }
    const source = [...entries]
        .sort((a, b) => (b.credit_amount ?? 0) - (a.credit_amount ?? 0))
        .find((e) => (e.credit_amount ?? 0) > 0 && accountOf(e)?.system_account_type !== 'fx_clearing');
    return source ? (accountOf(source)?.currency ?? null) : null;
}
/**
 * Format an amount in the account-on-side currency, falling back to the
 * organization's base currency (or EUR if neither is set).
 *
 * @param amount Amount to format
 * @param entries Ledger transaction entries
 * @param side Which side to read for the account currency
 * @param organization Organization with base currency (fallback)
 * @param language Viewer/document language driving the number convention
 */
export function formatEntryAccountCurrency(amount, entries, side, organization, language) {
    const currency = getEntryAccountCurrency(entries, side) ?? organization?.currency ?? 'EUR';
    return formatCurrency(amount, currency, localeForLanguage(language));
}
/**
 * Check if a currency is supported by ECB exchange rates
 * @param currency - Currency code to check
 * @returns True if currency is supported
 */
export function isSupportedCurrency(currency) {
    return SUPPORTED_CURRENCIES.includes(currency.toUpperCase());
}
/**
 * Sum amounts grouped by currency
 * @param items - Array of amounts with their currencies
 * @returns Object with currency codes as keys and summed amounts as values
 *
 * @example
 * ```ts
 * const items = [
 *   { amount: 100, currency: 'EUR' },
 *   { amount: 50, currency: 'USD' },
 *   { amount: 200, currency: 'EUR' },
 * ];
 * const totals = sumByCurrency(items);
 * // Result: { EUR: 300, USD: 50 }
 * ```
 */
export function sumByCurrency(items) {
    return items.reduce((acc, item) => {
        const currency = item.currency.toUpperCase();
        acc[currency] = (acc[currency] || 0) + item.amount;
        return acc;
    }, {});
}
/**
 * Add an amount to a multi-currency total
 * @param totals - Existing multi-currency totals
 * @param amount - Amount to add
 * @param currency - Currency of the amount
 * @returns New totals with the amount added
 */
export function addToMultiCurrencyTotal(totals, amount, currency) {
    const normalizedCurrency = currency.toUpperCase();
    return {
        ...totals,
        [normalizedCurrency]: (totals[normalizedCurrency] || 0) + amount,
    };
}
/**
 * Check if a multi-currency total is empty (no currencies or all zero)
 * @param totals - Multi-currency totals to check
 * @returns True if empty or all amounts are zero
 */
export function isMultiCurrencyTotalEmpty(totals) {
    const entries = Object.entries(totals);
    if (entries.length === 0)
        return true;
    return entries.every(([, amount]) => amount === 0);
}
/** The currencies of `totals` that carry a non-zero amount. */
export function nonZeroCurrencies(totals) {
    return Object.keys(totals).filter((code) => totals[code] !== 0);
}
/**
 * The one currency `totals` is in — its only non-zero currency, else (all
 * zero) its only key — or `undefined` when it spans several or is empty.
 */
export function soleCurrency(totals) {
    const nonZero = nonZeroCurrencies(totals);
    if (nonZero.length === 1)
        return nonZero[0];
    if (nonZero.length > 1)
        return undefined;
    const keys = Object.keys(totals);
    return keys.length === 1 ? keys[0] : undefined;
}
/**
 * Format multi-currency totals as a display string
 * @param totals - Object with currency codes and amounts
 * @param language - Viewer/document language driving the number convention
 * @param options - Formatting options
 * @returns Formatted string like "€12,500 + $5,000"
 *
 * @example
 * ```ts
 * const formatted = formatMultiCurrencyTotal({ EUR: 12500, USD: 5000 }, 'en');
 * // Result: "€12,500 + $5,000"
 * ```
 */
export function formatMultiCurrencyTotal(totals, language, options) {
    const { separator = ' + ', emptyText = '—', sortOrder = 'alphabetical' } = options || {};
    const entries = Object.entries(totals).filter(([, amount]) => amount !== 0);
    if (entries.length === 0) {
        return emptyText;
    }
    // Sort entries
    if (sortOrder === 'amount') {
        entries.sort(([, a], [, b]) => Math.abs(b) - Math.abs(a));
    }
    else {
        entries.sort(([a], [b]) => a.localeCompare(b));
    }
    const locale = localeForLanguage(language);
    return entries
        .map(([currency, amount]) => formatCurrency(amount, currency, locale))
        .join(separator);
}
/**
 * Compare two multi-currency totals for equality
 * Used for balance checks in financial reports
 * @param a - First multi-currency total
 * @param b - Second multi-currency total
 * @returns True if all currencies match with same amounts
 */
export function multiCurrencyTotalsEqual(a, b) {
    const allCurrencies = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const currency of allCurrencies) {
        const amountA = a[currency] || 0;
        const amountB = b[currency] || 0;
        // Use small epsilon for floating point comparison
        if (Math.abs(amountA - amountB) > 0.01) {
            return false;
        }
    }
    return true;
}
/**
 * Parse a user-typed number string using the given locale's decimal/group
 * separators. Unlike `parseFloat`, which always treats `.` as the decimal
 * separator, this respects EUR-style `1.234,56` and similar formats.
 *
 * Inverse of `Intl.NumberFormat(locale).format()` — which is what
 * `NumberDisplay` and `NumericCell` use for display. Intl has no native
 * parse, so we derive the separators from `formatToParts(12345.6)` and
 * transform the input string by hand.
 *
 * Tolerance: when the input has only `.` and no locale decimal (e.g. a
 * de-DE user types `1.23`), treat the `.` as decimal so en-US-style
 * habits keep working. Multiple `.` with no locale decimal (e.g. de-DE
 * `1.000.000`) are treated as group separators.
 */
export function parseLocalizedNumber(raw, locale) {
    if (raw === '' || raw == null)
        return null;
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const groupSep = parts.find((p) => p.type === 'group')?.value ?? '';
    const decimalSep = parts.find((p) => p.type === 'decimal')?.value ?? '.';
    let s = raw.trim();
    // Strip NBSP / thin-NBSP that some locales use as group separators
    // (e.g. fr-FR, sv-SE) before doing anything else.
    s = s.replace(/[\u00A0\u202F]/g, '');
    if (decimalSep === '.') {
        // en-US-style locale. parseFloat already works; stripping the group
        // separator (typically `,`) is enough.
        if (groupSep)
            s = s.split(groupSep).join('');
    }
    else {
        // Locale's decimal is something other than `.` (e.g. `,` for de-DE).
        const hasLocaleDecimal = s.includes(decimalSep);
        const dotCount = (s.match(/\./g) ?? []).length;
        if (hasLocaleDecimal) {
            // Unambiguous: `.` is group, locale-decimal is decimal.
            if (groupSep)
                s = s.split(groupSep).join('');
            s = s.split(decimalSep).join('.');
        }
        else if (dotCount > 1) {
            // Multiple `.` with no locale decimal → all are group separators
            // (e.g. de-DE "1.000.000"). Strip them.
            s = s.replace(/\./g, '');
        }
        else {
            // 0 or 1 `.` with no locale decimal — ambiguous. Treat `.` as a
            // decimal for backward compat (user typing en-US-style inside a
            // non-en-US org). Also strip any non-`.` group separator (e.g.
            // NBSP in fr-FR) just in case.
            if (groupSep && groupSep !== '.')
                s = s.split(groupSep).join('');
        }
    }
    const n = Number.parseFloat(s);
    return Number.isNaN(n) ? null : n;
}
//# sourceMappingURL=currency.js.map